/**
 * Ausgangsjournal am Beleg: Wann ist er auf welchem Weg an wen gegangen?
 *
 * E-Mail-Versand und PDF-Aufrufe traegt Canalis selbst ein. Was ausserhalb
 * passiert - Post, persoenliche Uebergabe, Fax, Kundenportal - erfasst der
 * Anwender hier mit Empfaenger und Datum. Eintraege werden nie geaendert;
 * ein Irrtum bekommt einen weiteren Eintrag mit Bemerkung.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DISPATCH_CHANNELS, dispatchChannelLabel, formatDate, today } from '@erp/shared';

import { api, ApiError } from '../api/client';
import type { Dispatch } from '../api/types';
import { useToast } from './toast';
import { Badge, Field, Select, TextInput } from './ui';

const MANUAL = DISPATCH_CHANNELS.filter((entry) => entry.manual);

export function DispatchJournalCard({ documentId, mayWrite }: { documentId: string; mayWrite: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [channel, setChannel] = useState(MANUAL[0]!.code);
  const [recipient, setRecipient] = useState('');
  const [sentOn, setSentOn] = useState(today());
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showForm, setShowForm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['dispatches', documentId],
    queryFn: () => api.get<{ dispatches: Dispatch[] }>(`/api/documents/${documentId}/dispatches`),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<{ dispatch: Dispatch }>(`/api/documents/${documentId}/dispatches`, {
        channel,
        recipient,
        sentOn,
        note: note || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatches', documentId] });
      setRecipient('');
      setNote('');
      setErrors({});
      setShowForm(false);
      toast.success('Ausgang erfasst.');
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Der Ausgang konnte nicht erfasst werden.');
      }
    },
  });

  const rows = data?.dispatches ?? [];
  const outbound = rows.filter((row) => row.channel !== 'print');

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <div>
          <h2>Ausgangsjournal</h2>
          <div className="faint" style={{ fontSize: 12.5 }}>
            Wann ist der Beleg auf welchem Weg an wen gegangen? E-Mail und PDF trägt Canalis selbst ein.
          </div>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          {outbound.length > 0 ? (
            <Badge kind="success">
              {outbound.length === 1 ? '1 Ausgang' : `${outbound.length} Ausgänge`}
            </Badge>
          ) : (
            <Badge>Noch nicht versandt</Badge>
          )}
          {mayWrite ? (
            <button type="button" className="btn btn-sm" onClick={() => setShowForm((current) => !current)}>
              {showForm ? 'Abbrechen' : 'Ausgang erfassen'}
            </button>
          ) : null}
        </div>
      </div>

      {showForm && mayWrite ? (
        <div className="card-body" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="form-grid">
            <Field label="Weg" span={3} error={errors.channel}>
              <Select value={channel} onChange={setChannel} options={MANUAL.map((entry) => ({ value: entry.code, label: entry.label }))} />
            </Field>
            <Field label="Empfänger" span={4} error={errors.recipient} hint="Person, Abteilung oder Anschrift">
              <TextInput value={recipient} onChange={setRecipient} placeholder="z. B. Buchhaltung, Frau Meier" />
            </Field>
            <Field label="Ausgangstag" span={2} error={errors.sentOn}>
              <TextInput type="date" value={sentOn} onChange={setSentOn} />
            </Field>
            <Field label="Bemerkung" span={3} error={errors.note}>
              <TextInput value={note} onChange={setNote} placeholder="z. B. mit Lieferschein zusammen" />
            </Field>
            <Field label=" " span={12}>
              <button type="button" className="btn btn-primary" disabled={add.isPending} onClick={() => add.mutate()}>
                {add.isPending ? 'Wird erfasst …' : 'Ausgang eintragen'}
              </button>
            </Field>
          </div>
        </div>
      ) : null}

      {isLoading ? null : rows.length === 0 ? (
        <div className="card-body faint" style={{ fontSize: 13 }}>
          Noch kein Ausgang. Der Versand per E-Mail und jeder PDF-Aufruf erscheinen hier automatisch.
        </div>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Zeitpunkt</th>
                <th>Weg</th>
                <th>Empfänger</th>
                <th>Betreff / Anhänge</th>
                <th>Durch</th>
                <th>Bemerkung</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={row.channel === 'print' ? { opacity: 0.7 } : undefined}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {formatDate(row.sentOn)}
                    <span className="faint"> {new Date(row.sentAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                  </td>
                  <td>
                    <Badge kind={row.channel === 'email' ? 'accent' : row.channel === 'print' ? 'neutral' : 'success'}>
                      {dispatchChannelLabel(row.channel)}
                    </Badge>
                  </td>
                  <td>{row.recipient ?? <span className="faint">–</span>}</td>
                  <td className="faint" style={{ fontSize: 12.5 }}>
                    {row.subject ? <div>{row.subject}</div> : null}
                    {row.attachments.length > 0 ? <div className="mono" style={{ fontSize: 11.5 }}>{row.attachments.join(', ')}</div> : null}
                  </td>
                  <td>{row.userName ?? ''}</td>
                  <td className="faint">{row.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
